import React from 'react';
import { TrendingUp, TrendingDown, Minus, Target, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { motion } from 'framer-motion';

const ScoreGauge = ({ score, size = 64 }) => {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  
  const getColor = () => {
    if (score >= 70) return '#10D183';
    if (score >= 40) return '#F9C80E';
    return '#EF4444';
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#E5E7EB" strokeWidth="4" />
        <circle
          cx={size/2} cy={size/2} r={radius} fill="none"
          stroke={getColor()} strokeWidth="4" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.8s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold" style={{ color: getColor() }}>{score}</span>
      </div>
    </div>
  );
};

const getLabelConfig = (label) => {
  switch (label) {
    case 'favorable':
      return { text: 'Favorable', bg: 'bg-emerald-50', textColor: 'text-emerald-700', border: 'border-emerald-200', icon: TrendingDown };
    case 'unfavorable':
      return { text: 'Défavorable', bg: 'bg-red-50', textColor: 'text-red-700', border: 'border-red-200', icon: TrendingUp };
    default:
      return { text: 'Neutre', bg: 'bg-amber-50', textColor: 'text-amber-700', border: 'border-amber-200', icon: Minus };
  }
};

const RecommendationCard = ({ rec, index = 0, compact = false }) => {
  const config = getLabelConfig(rec.label);
  const LabelIcon = config.icon;

  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
        className={`flex items-center gap-3 p-3 rounded-lg border ${config.border} ${config.bg}`}
        data-testid={`recommendation-compact-${rec.contract_name}`}
      >
        <ScoreGauge score={rec.score} size={40} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#191D1E] truncate">{rec.contract_name}</p>
          <p className="text-xs text-[#687175] truncate">{rec.reason}</p>
        </div>
        <span className={`text-xs font-semibold ${config.textColor} whitespace-nowrap`}>{rec.current_price} €</span>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06 }}
      className="bg-white rounded-2xl border border-[#BFC6CC]/12 p-5 hover:shadow-md transition-shadow"
      data-testid={`recommendation-card-${rec.contract_name}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${config.bg} ${config.textColor}`}>
              <LabelIcon className="w-3 h-3" />
              {config.text}
            </span>
            <span className="text-xs text-[#939BA0] px-2 py-0.5 rounded-full bg-[#F5F6F9]">
              {rec.energy_type === 'electricity' ? 'Électricité' : 'Gaz'}
            </span>
          </div>
          <h3 className="text-base font-bold text-[#191D1E]">{rec.contract_name}</h3>
          {rec.client_name && <p className="text-xs text-[#687175]">Client: {rec.client_name}</p>}
        </div>
        <ScoreGauge score={rec.score} size={56} />
      </div>

      <p className="text-sm text-[#687175] mb-4">{rec.reason}</p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-[#F5F6F9] rounded-lg p-2.5 text-center">
          <p className="text-xs text-[#939BA0] mb-0.5">Prix actuel</p>
          <p className="text-sm font-bold text-[#191D1E]">{rec.current_price} €</p>
        </div>
        <div className="bg-[#F5F6F9] rounded-lg p-2.5 text-center">
          <p className="text-xs text-[#939BA0] mb-0.5">Moy. 30j</p>
          <p className="text-sm font-bold text-[#191D1E]">{rec.avg_30d} €</p>
        </div>
        <div className="bg-[#F5F6F9] rounded-lg p-2.5 text-center">
          <p className="text-xs text-[#939BA0] mb-0.5">Tendance 7j</p>
          <p className={`text-sm font-bold flex items-center justify-center gap-1 ${rec.trend_7d <= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {rec.trend_7d <= 0 ? <ArrowDownRight className="w-3.5 h-3.5" /> : <ArrowUpRight className="w-3.5 h-3.5" />}
            {rec.trend_7d > 0 ? '+' : ''}{rec.trend_7d}%
          </p>
        </div>
        <div className="bg-[#F5F6F9] rounded-lg p-2.5 text-center">
          <p className="text-xs text-[#939BA0] mb-0.5">vs Moy.</p>
          <p className={`text-sm font-bold ${rec.pct_vs_avg <= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {rec.pct_vs_avg > 0 ? '+' : ''}{rec.pct_vs_avg}%
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default RecommendationCard;
